/**
 * PartialAttributionsView - Vue dédiée aux attributions partielles (bleu ciel).
 *
 * Une attribution partielle = une VENTE incomplètement servie par des ACHAT.
 * - Les ACHAT pré-attribués apparaissent en bleu ciel et sont déjà déduits du stock dispo.
 * - La VENTE compte pour (qty - linked) dans le delta (besoin restant).
 * - Quand linked >= qty, l'attribution bascule automatiquement en attribution complète.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, AlertCircle, Trash2, Plus, RefreshCw, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { ticketService } from '../../services/api';

const PartialAttributionsView = ({ project, onComplete, refreshKey = 0 }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState({});

  const load = async () => {
    if (!project) return;
    setLoading(true);
    try {
      const data = await ticketService.listPartialAttributions(project);
      setGroups(data?.groups || []);
    } catch (e) {
      toast.error("Impossible de charger les attributions partielles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [project, refreshKey]);

  const totals = useMemo(() => {
    return groups.reduce((acc, g) => {
      acc.linked += g.linked;
      acc.missing += g.missing;
      acc.totalVenteQty += g.qty;
      return acc;
    }, { linked: 0, missing: 0, totalVenteQty: 0 });
  }, [groups]);

  const toggle = (k) => setCollapsed(p => ({ ...p, [k]: !p[k] }));

  const handleDetach = async (achatIds) => {
    if (!achatIds || achatIds.length === 0) return;
    if (!window.confirm(`Détacher ${achatIds.length} ACHAT pré-attribué(s) ?\n\nL'ACHAT redeviendra libre et la VENTE perdra ces liens.`)) return;
    try {
      await ticketService.partialDetach(achatIds, project);
      toast.success(`${achatIds.length} ACHAT détaché(s)`);
      await load();
      onComplete?.();
    } catch (e) {
      toast.error("Échec du détachement");
    }
  };

  const handleComplete = (vente) => {
    // Ouvre le modal d'attribution standard pour compléter (signal au parent)
    onComplete?.(vente);
  };

  const exportCSV = () => {
    const rows = [['Ref Vente', 'Match', 'Catégorie', 'Qty Vente', 'Liés', 'Restants', 'Refs ACHAT liés', 'Client']];
    for (const g of groups) {
      const v = g.vente || {};
      rows.push([
        v.ref_vente || v.id || '',
        v.evenement || '',
        v.categorie || '',
        g.qty,
        g.linked,
        g.missing,
        (g.achats || []).map(a => a.ref_achat || '').filter(Boolean).join(' ; '),
        v.nom_client || '',
      ]);
    }
    const csv = rows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attributions_partielles_${(project || 'projet').replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exporté');
  };

  return (
    <div className="space-y-4" data-testid="partial-attributions-view">
      {/* Header */}
      <div className="flex items-center justify-between bg-zinc-900 rounded-lg px-4 py-3 flex-wrap gap-3 border border-sky-500/30 shadow-lg">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-sky-500/15 border border-sky-500/40 flex items-center justify-center">
            <AlertCircle className="h-5 w-5 text-sky-400" />
          </div>
          <div>
            <div className="text-sm font-semibold text-white">Attributions partielles en cours</div>
            <div className="text-xs text-zinc-300 mt-0.5">
              {groups.length === 0
                ? "Aucune attribution partielle"
                : <>
                    <strong className="text-sky-300">{groups.length}</strong> VENTE incomplète(s) ·
                    <strong className="text-emerald-300 ml-1">{totals.linked}</strong> ACHAT liés ·
                    <strong className="text-amber-300 ml-1">{totals.missing}</strong> manquant(s)
                  </>
              }
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={load} disabled={loading} className="h-8" data-testid="reload-partials">
            <RefreshCw className={cn("h-3.5 w-3.5 mr-1.5", loading && "animate-spin")} /> Recharger
          </Button>
          {groups.length > 0 && (
            <Button size="sm" onClick={exportCSV} className="h-8 bg-[#C4A052] hover:bg-[#D4B86A] text-black" data-testid="export-partials-csv">
              <Download className="h-3.5 w-3.5 mr-1.5" /> Exporter CSV
            </Button>
          )}
        </div>
      </div>

      {/* Empty state */}
      {!loading && groups.length === 0 && (
        <div className="text-center py-12 text-zinc-500">
          <AlertCircle className="h-12 w-12 mx-auto mb-3 text-sky-400/40" />
          <p className="text-lg">Aucune attribution partielle.</p>
          <p className="text-xs mt-1">Les pré-attributions effectuées depuis le modal d'attribution apparaîtront ici.</p>
        </div>
      )}

      {/* Liste */}
      <div className="space-y-2">
        {groups.map((g) => {
          const v = g.vente || {};
          const key = v.id || v.ref_vente;
          const isOpen = !collapsed[key];
          const progress = g.qty > 0 ? Math.round((g.linked / g.qty) * 100) : 0;
          return (
            <div
              key={key}
              className="rounded-lg border-2 border-sky-500/50 bg-zinc-900 overflow-hidden shadow-lg"
              data-testid={`partial-group-${key}`}
            >
              {/* Vente header */}
              <button
                onClick={() => toggle(key)}
                className="w-full flex items-center gap-3 px-4 py-3 bg-zinc-900 hover:bg-sky-500/5 transition-colors text-left"
              >
                {isOpen ? <ChevronDown className="h-4 w-4 text-sky-400 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-sky-400 flex-shrink-0" />}
                <span className="px-2 py-1 rounded text-xs font-bold uppercase tracking-wide bg-sky-500 text-white">
                  PARTIEL
                </span>
                <span className="font-mono text-sm font-bold text-sky-200 min-w-[100px]">
                  {v.ref_vente || '—'}
                </span>
                <span className="text-sm text-white truncate flex-1 min-w-0">
                  {v.evenement || '—'}
                </span>
                <span className="text-xs text-zinc-400 hidden md:inline">{v.categorie || '—'}</span>
                <div className="flex items-center gap-2 ml-auto">
                  <div className="flex items-center gap-2 bg-zinc-900/60 px-3 py-1 rounded">
                    <span className="text-sky-300 font-bold text-sm">{g.linked}</span>
                    <span className="text-zinc-500 text-xs">/</span>
                    <span className="text-zinc-300 text-sm">{g.qty}</span>
                    <div className="w-16 h-1.5 bg-zinc-700 rounded-full overflow-hidden ml-1">
                      <div className="h-full bg-sky-400 transition-all" style={{ width: `${progress}%` }} />
                    </div>
                  </div>
                  {g.missing > 0 && (
                    <span className="text-xs text-amber-300 font-bold bg-amber-500/20 px-2 py-1 rounded">
                      +{g.missing} restant{g.missing > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </button>

              {/* Détail (ACHAT liés) */}
              {isOpen && (
                <div className="border-t border-sky-500/30 bg-zinc-950 px-4 py-3 space-y-1.5">
                  {/* Actions globales */}
                  <div className="flex items-center justify-between mb-2 pb-2 border-b border-zinc-800">
                    <span className="text-[11px] text-zinc-400 uppercase tracking-wide font-semibold">
                      ACHAT pré-attribués ({(g.achats || []).length})
                    </span>
                    <div className="flex gap-2">
                      {g.missing > 0 && (
                        <Button
                          size="sm"
                          onClick={() => handleComplete(v)}
                          className="h-7 text-xs bg-sky-500 hover:bg-sky-600 text-white"
                          data-testid={`btn-complete-${key}`}
                        >
                          <Plus className="h-3 w-3 mr-1" /> Compléter ({g.missing})
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDetach((g.achats || []).map(a => a.id))}
                        className="h-7 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        data-testid={`btn-detach-all-${key}`}
                      >
                        <Trash2 className="h-3 w-3 mr-1" /> Tout détacher
                      </Button>
                    </div>
                  </div>

                  {(g.achats || []).length === 0 ? (
                    <div className="text-xs text-zinc-500 italic py-2">Aucun ACHAT lié à cette VENTE.</div>
                  ) : (
                    (g.achats || []).map((a) => (
                      <div
                        key={a.id}
                        className="grid grid-cols-12 gap-2 text-xs items-center py-1.5 px-2 rounded bg-zinc-900 hover:bg-sky-500/10"
                      >
                        <span className="col-span-2 font-mono font-bold text-sky-300 truncate">{a.ref_achat || '—'}</span>
                        <span className="col-span-2 text-zinc-200 truncate">{a.categorie || '—'}</span>
                        <span className="col-span-1 text-zinc-300">B:{a.block || '—'}</span>
                        <span className="col-span-1 text-zinc-300">R:{a.rang || '—'}</span>
                        <span className="col-span-1 text-zinc-300">P:{a.place || '—'}</span>
                        <span className="col-span-2 text-zinc-400 truncate">{a.source || '—'}</span>
                        <span className="col-span-2 text-zinc-400 truncate">{a.email_compte || '—'}</span>
                        <div className="col-span-1 flex justify-end">
                          <button
                            onClick={() => handleDetach([a.id])}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded p-1"
                            title="Détacher cet ACHAT"
                            data-testid={`btn-detach-${a.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PartialAttributionsView;
